/**
 * VENDORED from tc-console/src/mdx/mdxComponents.tsx — DO NOT EDIT.
 *
 * Refresh with scripts/sync-home.sh; edit the canonical file instead. See the
 * script header for what is copied verbatim and what is an adaptation.
 */
/**
 * mdxComponents.tsx — the default element map: markdown constructs as MUI.
 *
 * Every standard markdown element renders through a themed MUI component, so a
 * plain `.md`/`.mdx` page reads as a polished document in the product's own
 * typography, palette, spacing and radius — light and dark — instead of as
 * lightly styled browser HTML. Nothing here hard-codes a visual value the
 * theme already provides: type scale from theme variants, spacing from the
 * theme scale, color from the palette, corners from theme shape.
 *
 * TWO DENSITIES. `article` is the document surface: full-size headings with
 * real hierarchy, generous vertical rhythm, primary text — what an opened
 * page gets. `compact` is the card surface: the tighter scale tile/section/
 * callout bodies always had, so cards keep their look while gaining the rich
 * elements (tables, code, figures, task lists) they never rendered before.
 *
 * LINKS. When the host supplies `onLink`, every link becomes an in-place
 * action (the corpus-navigation contract: the host decides corpus page vs.
 * external tab). Without `onLink`, links are real anchors — external http(s)
 * opens a new tab with noopener. URL safety itself is upstream: react-markdown
 * strips dangerous schemes before an href ever reaches this map.
 *
 * SEMANTICS. Heading levels, lists, tables, figures and code keep their real
 * HTML elements (Typography/Box `component` props) — accessibility is the
 * markup's job, not a bolt-on.
 */

import { Children, isValidElement, type ReactElement, type ReactNode } from 'react'
import {
  Box, Divider, Link, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { Components } from 'react-markdown'

export type MdxDensity = 'article' | 'compact'

export interface MdxComponentsOptions {
  density?: MdxDensity
  /** In-place link handling (corpus navigation). Absent = real anchors. */
  onLink?: (href: string) => void
  /** Fenced-block dispatch, wired by MDXContent from the registry. */
  renderBlock?: (code: string, language: string) => ReactNode | undefined
}

const MONO_STACK = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

/** The fence language of a <pre>'s code child, lowercase, when present. */
function fenceLanguage(children: ReactNode): { code: string; language?: string } {
  const child = Children.toArray(children).find(isValidElement) as
    | ReactElement<{ className?: string; children?: ReactNode }>
    | undefined
  const raw = child?.props?.children
  const code = Array.isArray(raw) ? raw.join('') : String(raw ?? '')
  const language = /language-([a-z0-9-]+)/i.exec(child?.props?.className ?? '')?.[1]?.toLowerCase()
  return { code, language }
}

export function createMdxComponents(options: MdxComponentsOptions = {}): Components {
  const { density = 'article', onLink, renderBlock } = options
  const article = density === 'article'

  const bodyVariant = article ? 'body1' : 'body2'
  const bodyColor = article ? 'text.primary' : 'text.secondary'
  const textGap = article ? 1.5 : 1
  const headGap = article ? 3 : 2

  return {
    // ── Headings: semantic level preserved, scale from the theme ────────────
    h1: ({ children }) => (
      <Typography component="h1" variant={article ? 'h4' : 'h6'} fontWeight={800}
        sx={{ mt: headGap, mb: 1, letterSpacing: article ? -0.4 : undefined, '&:first-child': { mt: 0 } }}>
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography component="h2" variant={article ? 'h5' : 'subtitle1'} fontWeight={800}
        sx={(t) => ({
          mt: headGap + 0.5,
          mb: 1,
          '&:first-child': { mt: 0 },
          ...(article
            ? { pb: 0.5, borderBottom: `1px solid ${t.palette.divider}` }
            : {}),
        })}>
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography component="h3" variant={article ? 'h6' : 'subtitle2'} fontWeight={700}
        sx={{ mt: headGap, mb: 0.5, '&:first-child': { mt: 0 } }}>
        {children}
      </Typography>
    ),
    h4: ({ children }) => (
      <Typography component="h4" variant={article ? 'subtitle1' : 'body1'} fontWeight={700}
        sx={{ mt: headGap - 0.5, mb: 0.5, '&:first-child': { mt: 0 } }}>
        {children}
      </Typography>
    ),
    h5: ({ children }) => (
      <Typography component="h5" variant={article ? 'subtitle2' : 'body2'} fontWeight={700}
        sx={{ mt: headGap - 0.5, '&:first-child': { mt: 0 } }}>
        {children}
      </Typography>
    ),
    h6: ({ children }) => (
      <Typography component="h6" variant="body2" fontWeight={700} color="text.secondary"
        sx={{ mt: headGap - 0.5, textTransform: article ? 'uppercase' : undefined, letterSpacing: article ? '0.06em' : undefined, '&:first-child': { mt: 0 } }}>
        {children}
      </Typography>
    ),

    // ── Prose ────────────────────────────────────────────────────────────────
    p: ({ children }) => (
      <Typography component="p" variant={bodyVariant} color={bodyColor}
        sx={{ my: textGap, '&:first-of-type': { mt: 0 }, '&:last-child': { mb: 0 } }}>
        {children}
      </Typography>
    ),
    strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 700, color: 'text.primary' }}>{children}</Box>,
    em: ({ children }) => <Box component="em">{children}</Box>,
    del: ({ children }) => <Box component="del" sx={{ color: 'text.secondary' }}>{children}</Box>,
    a: ({ href, children }) =>
      onLink ? (
        <Link
          component="button"
          sx={{ fontWeight: 600, textAlign: 'left', textUnderlineOffset: 2, verticalAlign: 'baseline' }}
          onClick={() => onLink(href ?? '')}
        >
          {children}
        </Link>
      ) : (
        <Link
          href={href}
          sx={{ fontWeight: 600, textUnderlineOffset: 2 }}
          {...(/^(https?:)/i.test(href ?? '') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {children}
        </Link>
      ),

    // ── Lists: marker color from the palette; GFM task lists render as tasks ──
    ul: ({ children }) => (
      <Box
        component="ul"
        sx={{
          pl: 2.75, my: textGap, color: bodyColor,
          '& li::marker': { color: 'primary.main' },
          '& li:has(> input[type="checkbox"])': { listStyle: 'none', marginLeft: -2.25 },
        }}
      >
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ pl: 2.75, my: textGap, color: bodyColor, '& li::marker': { color: 'primary.main', fontWeight: 600 } }}>
        {children}
      </Box>
    ),
    li: ({ children }) => (
      <Typography component="li" variant={bodyVariant} color={bodyColor} sx={{ my: 0.5, pl: 0.5 }}>
        {children}
      </Typography>
    ),
    input: ({ checked }) => (
      <Box
        component="input"
        type="checkbox"
        disabled
        checked={checked}
        aria-checked={checked}
        sx={(t) => ({ accentColor: t.palette.primary.main, mr: 0.75, verticalAlign: 'middle' })}
      />
    ),

    // ── Blockquote: accent-ruled card, clearly not body text ─────────────────
    blockquote: ({ children }) => (
      <Box
        component="blockquote"
        sx={(t) => ({
          m: 0,
          my: textGap + 0.5,
          px: 2.5,
          py: 1.5,
          borderLeft: '4px solid',
          borderColor: alpha(t.palette.primary.main, 0.45),
          bgcolor: t.palette.mode === 'dark' ? alpha(t.palette.primary.main, 0.12) : alpha(t.palette.primary.main, 0.06),
          borderRadius: `0 ${t.shape.borderRadius * 2}px ${t.shape.borderRadius * 2}px 0`,
          color: 'text.secondary',
        })}
      >
        {children}
      </Box>
    ),

    hr: () => <Divider sx={{ my: article ? 4 : 2 }} />,

    // ── Code: inline chip vs. block sheet; fenced blocks dispatch first ──────
    code: ({ children }) => (
      <Box
        component="code"
        sx={{
          fontFamily: MONO_STACK,
          fontSize: '0.85em',
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
          px: 0.75,
          py: 0.125,
          borderRadius: 1,
          wordBreak: 'break-word',
        }}
      >
        {children}
      </Box>
    ),
    pre: ({ children }) => {
      const { code, language } = fenceLanguage(children)
      if (language && renderBlock) {
        const rendered = renderBlock(code, language)
        if (rendered !== undefined) return <>{rendered}</>
      }
      return (
        <Paper
          component="pre"
          variant="outlined"
          sx={(t) => ({
            my: textGap + 0.5,
            p: 2,
            borderRadius: 2,
            overflow: 'auto',
            bgcolor: t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.04) : t.palette.grey[50],
            '& code': {
              fontFamily: MONO_STACK,
              fontSize: '0.8125rem',
              lineHeight: 1.7,
              display: 'block',
              bgcolor: 'transparent',
              border: 0,
              p: 0,
            },
          })}
        >
          {children}
        </Paper>
      )
    },

    // ── Tables: a real sheet — header fill, rounded frame, row hover ─────────
    table: ({ children }) => (
      <TableContainer component={Paper} variant="outlined" sx={{ my: textGap + 0.5, borderRadius: 2 }}>
        <Table size="small" sx={{ '& tbody tr:hover': { bgcolor: 'action.hover' } }}>
          {children}
        </Table>
      </TableContainer>
    ),
    thead: ({ children }) => <TableHead>{children}</TableHead>,
    tbody: ({ children }) => <TableBody>{children}</TableBody>,
    tr: ({ children }) => <TableRow>{children}</TableRow>,
    th: ({ children }) => (
      <TableCell component="th" scope="col" sx={{ fontWeight: 700, bgcolor: 'action.hover', whiteSpace: 'nowrap' }}>
        {children}
      </TableCell>
    ),
    td: ({ children }) => <TableCell>{children}</TableCell>,

    // ── Images: responsive figures; the title attribute becomes a caption ────
    img: ({ src, alt, title }) => (
      <Box component="figure" sx={{ m: 0, my: textGap + 1, textAlign: 'center' }}>
        <Box
          component="img"
          src={src}
          alt={alt ?? ''}
          title={title}
          sx={{ maxWidth: '100%', height: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider', verticalAlign: 'middle' }}
        />
        {title && (
          <Typography component="figcaption" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {title}
          </Typography>
        )}
      </Box>
    ),
  }
}
